import LoadFailure from '../components/Brand/LoadFailure';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Layout, Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellOnIcon, HeartIcon, RepostIcon, FollowIcon, PlusIcon } from '../components/Brand/Icon';
import UserImage from '../components/UserImage';
import BrandLoader from '../components/Brand/BrandLoader';
import WiwHeaderTitle from '../components/WiwHeaderTitle';
import ButtonPanelReaction from '../components/Buttons/ButtonPanelReaction';
import usePageLayout from '../hooks/usePageLayout';
import { getUser, getLocalStorage, createName } from '../utils/func';
import config from '../utils/config';
import url from '../utils/url';
import { notificationRequest, notificationTarget } from './api';
import { useNotifications } from './NotificationsProvider';
import { observeNotifications } from './seen';
import './notifications.scss';

const { Header, Content, Footer } = Layout;
const icons = { likes: HeartIcon, reposts: RepostIcon, subscriptions: FollowIcon, author_comments: BellOnIcon };
export default function NotificationsPage() {
  const header = useRef(null), footer = useRef(null), content = useRef(null), sentinel = useRef(null);
  const pending = useRef(false), abort = useRef(null), mounted = useRef(false);
  const seenIds = useRef(new Set()), seenTimer = useRef(null), seenRequest = useRef(null);
  const newest = useRef(null), syncing = useRef(false), scrollAnchor = useRef(null), syncAbort = useRef(null);
  const listRef = useRef([]);
  const [list, setList] = useState([]), [cursor, setCursor] = useState(undefined);
  const [loading, setLoading] = useState(true), [error, setError] = useState(false);
  const navigate = useNavigate(), { t } = useTranslation(), { refresh } = useNotifications();
  listRef.current = list;
  const rememberScroll = useCallback(() => {
    const root = content.current;
    if (!root || root.scrollTop <= 0 || scrollAnchor.current) return;
    const top = root.getBoundingClientRect().top;
    // Keep a few visible successors too, in case the first visible card is removed.
    scrollAnchor.current = { top: root.scrollTop, cards: [...root.querySelectorAll('[data-notification-id]')]
      .filter(node => node.getBoundingClientRect().bottom > top).slice(0, 4)
      .map(node => ({ id: node.dataset.notificationId, offset: node.getBoundingClientRect().top - top })) };
  }, []);
  const userId = getUser()?.id, token = getLocalStorage(config.token);
  const session = useRef(null); session.current = { userId, token };
  const { contentHeight, contentTop } = usePageLayout(header, footer);
  const load = useCallback(async (before) => {
    if (!userId || !token || pending.current) return;
    pending.current = true; setLoading(true); setError(false);
    const controller = new AbortController(); abort.current = controller;
    try {
      const result = await notificationRequest(before ? `?cursor=${before}` : '', { signal: controller.signal, token });
      if (!mounted.current || controller.signal.aborted) return;
      setList(old => before ? [...old, ...result.list.filter(row => !old.some(item => item.id === row.id))] : result.list);
      if (!before) newest.current = result.list[0]?.id || null;
      setCursor(result.next_cursor);
    } catch { if (mounted.current && !controller.signal.aborted) setError(true); }
    finally { if (mounted.current && !controller.signal.aborted) { pending.current = false; setLoading(false); } }
  }, [userId, token]);

  useEffect(() => {
    mounted.current = true; pending.current = false; syncing.current = false; newest.current = null; seenIds.current.clear();
    scrollAnchor.current = null;
    setList([]); setCursor(undefined);
    if (!userId || !token) navigate(`${url('/login')}?from=${encodeURIComponent(url('/notifications'))}`, { replace: true });
    else void load();
    return () => { mounted.current = false; abort.current?.abort(); syncAbort.current?.abort(); seenRequest.current?.abort();
      seenRequest.current = null; clearTimeout(seenTimer.current); seenTimer.current = null; };
  }, [userId, token, load, navigate]);

  useEffect(() => {
    const sync = async () => {
      if (!userId || !token || pending.current || syncing.current || document.visibilityState === 'hidden') return;
      if (!newest.current) { void load(); return; }
      syncing.current = true;
      const controller = new AbortController(); syncAbort.current = controller;
      try {
        const ids = listRef.current.map(item => item.id);
        for (let start = 0; start < ids.length; start += 100) {
          const batch = ids.slice(start, start + 100);
          const result = await notificationRequest('/sync', { method: 'POST', body: { ids: batch }, signal: controller.signal, token });
          if (!mounted.current || controller.signal.aborted) return;
          if (!Array.isArray(result.list)) throw new Error('Invalid notification sync');
          const checked = new Set(batch), fresh = new Map(result.list.map(row => [row.id, row]));
          rememberScroll();
          setList(old => old.flatMap(row => !checked.has(row.id) ? [row] : fresh.has(row.id)
            ? [{ ...fresh.get(row.id), seen: row.seen || fresh.get(row.id).seen }] : []));
        }
        // Read forward so even more than one page of new activity cannot leave
        // a gap between the existing list and newly received notifications.
        for (let page = 0; page < 10; page++) {
          const result = await notificationRequest(`?after=${newest.current}`, { signal: controller.signal, token });
          if (!mounted.current || controller.signal.aborted || !result.list.length) break;
          rememberScroll();
          newest.current = Math.max(newest.current, ...result.list.map(row => row.id));
          setList(old => [...result.list.filter(row => !old.some(item => item.id === row.id)), ...old].sort((a, b) => b.id - a.id));
          if (!result.next_after) break;
        }
      } catch { /* Background refresh leaves the current list usable. */ }
      finally { if (syncAbort.current === controller) syncing.current = false; }
    };
    window.addEventListener('wiw:notifications-refresh', sync);
    return () => { window.removeEventListener('wiw:notifications-refresh', sync); syncAbort.current?.abort(); };
  }, [userId, token, load, rememberScroll]);

  useLayoutEffect(() => {
    const root = content.current, anchor = scrollAnchor.current;
    if (root && anchor) {
      const saved = anchor.cards.find(card => root.querySelector(`[data-notification-id="${card.id}"]`));
      if (saved) {
        const node = root.querySelector(`[data-notification-id="${saved.id}"]`);
        root.scrollTop += node.getBoundingClientRect().top - root.getBoundingClientRect().top - saved.offset;
      } else root.scrollTop = anchor.top;
    }
    scrollAnchor.current = null;
  }, [list]);

  useEffect(() => {
    if (!sentinel.current || !window.IntersectionObserver || cursor == null || loading || error) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) void load(cursor); }, { root: content.current, rootMargin: '120px' });
    observer.observe(sentinel.current); return () => observer.disconnect();
  }, [cursor, loading, error, load]);

  const flushSeen = useCallback(async function flush() {
    seenTimer.current = null;
    if (seenRequest.current || !seenIds.current.size || !mounted.current || !token
      || session.current.userId !== userId || session.current.token !== token) return;
    const controller = new AbortController(); seenRequest.current = controller;
    const ids = [...seenIds.current].slice(0, 100), checked = new Set(ids);
    ids.forEach(id => seenIds.current.delete(id));
    const current = () => mounted.current && !controller.signal.aborted
      && session.current.userId === userId && session.current.token === token;
    let failed = false;
    try {
      await notificationRequest('/seen', { method: 'POST', body: { ids }, token, signal: controller.signal });
      if (current()) {
        setList(old => old.map(row => checked.has(row.id) && !row.seen ? { ...row, seen: true } : row));
        void refresh(true);
      }
    } catch {
      if (current()) { failed = true; ids.forEach(id => seenIds.current.add(id)); }
    } finally {
      if (seenRequest.current === controller) seenRequest.current = null;
      if (current() && seenIds.current.size) seenTimer.current = setTimeout(flush, failed ? 10000 : 120);
    }
  }, [userId, token, refresh]);

  useEffect(() => observeNotifications(content.current, id => {
    seenIds.current.add(id);
    if (!seenTimer.current && !seenRequest.current) seenTimer.current = setTimeout(flushSeen, 120);
  }), [list, flushSeen]);

  return <Layout className="app-layout user-page notifications-page">
    <Header ref={header} className="header"><WiwHeaderTitle title={t('notifications:title')} /></Header>
    <Content ref={content} className="content-layot wrapper-padding notifications-content" style={{ top: contentTop, height: contentHeight }}>
      {error && <LoadFailure onRetry={() => load(list.length ? cursor : undefined)} />}
      <div className="notifications-list">
        {list.map(item => {
          const Drawing = icons[item.kind] || BellOnIcon, target = notificationTarget(item.target);
          const label = t(`notifications:${item.kind}`, { name: createName(item.actor) || `@${item.actor.user_name}` });
          return <article key={item.id} className="notification-card" data-notification-id={item.id} data-seen={item.seen}>
            <Link className="notification-avatar" to={url(`/user/${encodeURIComponent(item.actor.user_name)}`)} aria-label={`@${item.actor.user_name}`}>
              <UserImage picture={item.actor.picture} />
            </Link>
            <Link className="notification-target" to={url(target || '/notifications')}>
              <Drawing /><span>{label}</span>
            </Link>
            {!item.seen && <PlusIcon className="notification-new" aria-label={t('notifications:newActivity')} />}
          </article>;
        })}
      </div>
      {!loading && !error && !list.length && <p className="notifications-empty">{t('notifications:empty')}</p>}
      {loading && <BrandLoader />}
      {cursor != null && !error && <div ref={sentinel} className="notifications-more"><Button onClick={() => load(cursor)} disabled={loading} aria-label={t('brand:more', { defaultValue: 'More' })}>…</Button></div>}
    </Content>
    <Footer ref={footer}><ButtonPanelReaction /></Footer>
  </Layout>;
}
